import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import KpiWidget from "@/components/dashboard/widgets/KpiWidget";
import ChartWidget from "@/components/dashboard/widgets/ChartWidget";
import TableWidget from "@/components/dashboard/widgets/TableWidget";
import PieWidget from "@/components/dashboard/widgets/PieWidget";
import DrilldownModal from "@/components/dashboard/DrilldownModal";
import DashboardCustomizationPanel from "@/components/dashboard/DashboardCustomizationPanel";

const POLL_MS = 30_000;

const WIDGET_COMPONENTS = {
  kpi: KpiWidget,
  chart: ChartWidget,
  table: TableWidget,
  pie: PieWidget,
};

const SIZE_TO_SPAN = {
  small: "col-span-1",
  medium: "col-span-1 sm:col-span-2",
  large: "col-span-1 sm:col-span-2 xl:col-span-4",
};

/**
 * UniversalDashboardPanel — orchestrates:
 *   • Widget catalog fetch (respects RBAC on the backend).
 *   • Per-user layout persistence (GET/PUT /api/dashboard/layout).
 *   • Individual widget data fetches, polled every 30s.
 *   • Page Visibility API — pauses polling when tab hidden.
 *   • Drill-down modal + navigation via `onNavigate` callback.
 */
export default function UniversalDashboardPanel({ onNavigate }) {
  const [catalog, setCatalog] = useState([]);
  const [layout, setLayout] = useState([]);
  const [payloads, setPayloads] = useState({});
  const [loading, setLoading] = useState(true);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [drilldownState, setDrilldownState] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const visibleRef = useRef(!document.hidden);

  const catalogById = useMemo(() => {
    const m = {};
    for (const w of catalog) m[w.id] = w;
    return m;
  }, [catalog]);

  const enabledEntries = useMemo(
    () => layout.filter((e) => e.enabled && catalogById[e.widget_id]),
    [layout, catalogById]
  );

  // Bootstrap: fetch catalog + layout in parallel.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const [catRes, layRes] = await Promise.all([
          axios.get(`${API}/dashboard/widgets/catalog`),
          axios.get(`${API}/dashboard/layout`),
        ]);
        if (cancelled) return;
        setCatalog(catRes.data.widgets || []);
        setLayout(layRes.data.layout || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Error cargando dashboard");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const refreshAll = useCallback(async () => {
    if (!enabledEntries.length) return;
    const results = await Promise.allSettled(
      enabledEntries.map((e) =>
        axios.get(`${API}/dashboard/widget/${e.widget_id}/data`)
      )
    );
    const next = {};
    results.forEach((r, i) => {
      const wid = enabledEntries[i].widget_id;
      if (r.status === "fulfilled") next[wid] = r.value.data;
      else next[wid] = { error: r.reason?.response?.data?.detail || "Error" };
    });
    setPayloads((prev) => ({ ...prev, ...next }));
    setLastRefresh(new Date());
  }, [enabledEntries]);

  // Initial fetch (once layout resolved) + polling with Page Visibility API.
  useEffect(() => {
    if (loading) return;
    refreshAll();
    const tick = () => {
      if (visibleRef.current) refreshAll();
    };
    const id = setInterval(tick, POLL_MS);

    const onVis = () => {
      visibleRef.current = !document.hidden;
      if (visibleRef.current) refreshAll(); // catch-up on return
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [loading, refreshAll]);

  const persistLayout = useCallback(async (next) => {
    setLayout(next);
    try {
      await axios.put(`${API}/dashboard/layout`, { layout: next });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar el layout");
    }
  }, []);

  const resetLayout = useCallback(async () => {
    try {
      await axios.delete(`${API}/dashboard/layout`);
      const layRes = await axios.get(`${API}/dashboard/layout`);
      setLayout(layRes.data.layout || []);
      toast.success("Layout restablecido");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  }, []);

  const handleDrilldown = useCallback((definition, payload) => {
    setDrilldownState({ definition, payload });
  }, []);

  const handleNavigate = useCallback(
    (drilldown) => {
      setDrilldownState(null);
      onNavigate?.(drilldown);
    },
    [onNavigate]
  );

  if (loading) {
    return (
      <div className="py-24 text-center font-mono-tech text-sm text-[var(--text-secondary)]">
        Cargando tablero…
      </div>
    );
  }

  return (
    <div data-testid="universal-dashboard-panel" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="label-overline mb-1">DSH / UNIVERSAL</div>
          <h1 className="font-display text-3xl sm:text-5xl font-black tracking-tighter">
            Tablero Universal
          </h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1">
            Widgets modulares con actualización automática cada 30s. Haz click
            en cualquier widget para explorar el detalle.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="font-mono-tech text-[0.6rem] tracking-wider text-[var(--text-secondary)]">
            {lastRefresh
              ? `Última actualización: ${lastRefresh.toLocaleTimeString()}`
              : "—"}
          </div>
          <button
            data-testid="dashboard-refresh-btn"
            onClick={refreshAll}
            className="font-mono-tech text-xs border border-[var(--border)] px-3 py-1.5 hover:bg-black hover:text-white transition-colors"
            title="Refrescar"
          >
            ↻ Refrescar
          </button>
          <button
            data-testid="dashboard-customize-btn"
            onClick={() => setCustomizeOpen(true)}
            className="font-display font-bold text-sm bg-black text-white px-4 py-1.5 hover:bg-[#0E5FFF] transition-colors"
          >
            Personalizar
          </button>
        </div>
      </div>

      {enabledEntries.length === 0 ? (
        <div className="py-16 text-center border border-dashed border-[var(--border)] bg-white">
          <div className="font-display font-bold text-lg">
            No hay widgets activos
          </div>
          <p className="text-sm text-[var(--text-secondary)] mt-1">
            Abre <b>Personalizar</b> y activa los widgets que quieras ver.
          </p>
          <button
            onClick={() => setCustomizeOpen(true)}
            className="mt-4 font-display font-bold text-sm bg-black text-white px-4 py-2 hover:bg-[#0E5FFF] transition-colors"
          >
            Personalizar
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-px bg-[var(--border)]">
          {enabledEntries.map((entry) => {
            const def = catalogById[entry.widget_id];
            if (!def) return null;
            const Comp = WIDGET_COMPONENTS[def.type];
            if (!Comp) return null;
            const payload = payloads[def.id];
            const span = SIZE_TO_SPAN[def.size] || SIZE_TO_SPAN.medium;
            return (
              <div key={def.id} className={span}>
                {payload?.error ? (
                  <div className="bg-white p-5 min-h-[160px]">
                    <div className="font-mono-tech text-[0.6rem] tracking-wider text-[var(--alert-overdue,#FF3B30)]">
                      ERROR
                    </div>
                    <div className="font-display font-bold text-sm mt-1">
                      {def.title}
                    </div>
                    <div className="text-xs text-[var(--text-secondary)] mt-2">
                      {payload.error}
                    </div>
                  </div>
                ) : payload ? (
                  <Comp
                    definition={def}
                    payload={payload}
                    onDrilldown={handleDrilldown}
                  />
                ) : (
                  <div className="bg-white p-5 min-h-[160px] flex items-center justify-center font-mono-tech text-xs text-[var(--text-secondary)]">
                    Cargando…
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <DashboardCustomizationPanel
        open={customizeOpen}
        onClose={() => setCustomizeOpen(false)}
        layout={layout}
        catalog={catalog}
        onChange={persistLayout}
        onReset={resetLayout}
      />

      <DrilldownModal
        open={!!drilldownState}
        definition={drilldownState?.definition}
        payload={drilldownState?.payload}
        onClose={() => setDrilldownState(null)}
        onNavigate={handleNavigate}
      />
    </div>
  );
}
